/**
 * The one room list: each room's status in a word, search, and sorting.
 *
 * Home and Recons were two lists of the same rooms, sorted differently, and
 * only one said which rooms needed you. Pure and dependency-free so CRA's jest
 * can test it.
 */
import type { BoardroomSummary } from '../types';
import { waitingOn } from './boardroomProgress';

export type RoomStatus = 'needs_you' | 'digging' | 'idle' | 'empty';

export const STATUS_WORD: Record<RoomStatus, string> = {
  needs_you: 'needs you',
  digging: 'digging',
  idle: 'idle',
  empty: 'empty',
};

const time = (s: string) => new Date(s).getTime() || 0;

/** A room untouched this long stops counting as needing you. It still says
 *  what is waiting; it just is not urgent. */
export const QUIET_DAYS = 14;

/** Whether nothing has changed in the room for `QUIET_DAYS`. */
export function isQuiet(r: BoardroomSummary, now: number = Date.now()): boolean {
  // The newer of the last item and the room itself: renaming a room or
  // changing its question is someone working on it.
  const at = Math.max(time(r.waiting?.active_at ?? ''), time(r.updated_at));
  return now - at > QUIET_DAYS * 86_400_000;
}

/** What a room is doing, in one word. A room that is digging is not waiting
 *  on you, whatever has landed: its own seats are about to read it. Nor is
 *  one nobody has touched in two weeks. */
export function roomStatus(r: BoardroomSummary, now: number = Date.now()): RoomStatus {
  if (r.run?.status === 'RUNNING') return 'digging';
  const w = waitingOn(r);
  if ((w.results > 0 || w.proposals > 0) && !isQuiet(r, now)) return 'needs_you';
  const found = Object.values(r.item_counts ?? {}).some(n => n > 0) || r.items_created > 0;
  return found ? 'idle' : 'empty';
}

/** Why a room needs you, in words. */
export function waitingLine(r: BoardroomSummary): string {
  const { results, proposals } = waitingOn(r);
  const parts: string[] = [];
  if (results > 0) parts.push(`${results} result${results === 1 ? '' : 's'} waiting to be read`);
  if (proposals > 0) parts.push(`${proposals} setup change${proposals === 1 ? '' : 's'} to accept`);
  return parts.join(' · ');
}

export type RoomSort = 'status' | 'updated' | 'name';

const STATUS_ORDER: Record<RoomStatus, number> = { needs_you: 0, digging: 1, idle: 2, empty: 3 };


/** Rooms whose name or description has every word typed, in any order. */
export function searchRooms(rooms: BoardroomSummary[], q: string): BoardroomSummary[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return rooms;
  return rooms.filter(r => {
    const text = `${r.name} ${r.description ?? ''}`.toLowerCase();
    return words.every(w => text.includes(w));
  });
}

/** Sorted for reading. By status: what needs you first, then what is digging,
 *  each newest first. */
export function sortRooms(rooms: BoardroomSummary[], by: RoomSort): BoardroomSummary[] {
  const out = rooms.slice();
  if (by === 'name') return out.sort((a, b) => a.name.localeCompare(b.name));
  if (by === 'updated') return out.sort((a, b) => time(b.updated_at) - time(a.updated_at));
  return out.sort(
    (a, b) => STATUS_ORDER[roomStatus(a)] - STATUS_ORDER[roomStatus(b)] || time(b.updated_at) - time(a.updated_at),
  );
}

/** The list as it is drawn: the rooms with something in them, and the empty
 *  ones folded into a line underneath. */
export function splitEmpty(rooms: BoardroomSummary[]): { rooms: BoardroomSummary[]; empty: BoardroomSummary[] } {
  return {
    rooms: rooms.filter(r => roomStatus(r) !== 'empty'),
    empty: rooms.filter(r => roomStatus(r) === 'empty'),
  };
}
